/** Stamp — the original "Pull Shapes": stamp your vector set along the stroke. */
import type { CreateModule, ModuleManifest } from '../../core/module-api';
import { cfgBool, cfgNum, cfgStr } from '../../core/module-api';
import type { Point, Shape } from '../../core/scene';
import { defaultStyle } from '../../core/scene';
import { distance, resampleByArcLength, transformPath } from '../../core/geometry';
import type { ShapeItem, ShapeLibrary } from '../../core/shape-library';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 19h14"/><circle cx="7" cy="9" r="2.4"/><path d="M13 7l3 5h-6z"/></svg>';

const MAX_STAMPS = 500; // per-stroke cap: bounds bake/autosave/undo cost

/** Keep a stamp module's `set` dropdown in sync with the library's sets. */
export function syncStampSetOptions(manifest: ModuleManifest, lib: ShapeLibrary): void {
  const setParam = manifest.params?.find((p) => p.key === 'set');
  if (setParam && setParam.kind === 'select') setParam.options = lib.list();
}

export function createStamp(lib: ShapeLibrary): CreateModule {
  let pts: Point[] = [];
  let drawing = false;

  return {
    manifest: {
      id: 'stamp',
      kind: 'create',
      name: 'Stamp',
      apiVersion: 1,
      description: 'Stamp shapes from a set along your stroke — import your own SVGs.',
      icon: ICON,
      capabilities: ['random', 'image'],
      params: [
        { kind: 'select', key: 'set', label: 'Set', default: lib.firstId(), options: lib.list() },
        { kind: 'slider', key: 'spacing', label: 'Spacing', min: 4, max: 200, step: 1, default: 40 },
        { kind: 'slider', key: 'size', label: 'Size', min: 4, max: 200, step: 1, default: 38 },
        { kind: 'slider', key: 'scaleJitter', label: 'Scale ±', min: 0, max: 1, step: 0.01, default: 0 },
        { kind: 'slider', key: 'rotJitter', label: 'Spin', min: 0, max: 1, step: 0.01, default: 0 },
        { kind: 'toggle', key: 'align', label: 'Align', default: false },
      ],
    },

    onPointerDown(s) {
      pts = [{ x: s.x, y: s.y }];
      drawing = true;
    },

    onPointerMove(s) {
      if (!drawing) return;
      const p = { x: s.x, y: s.y };
      const last = pts[pts.length - 1];
      if (!last || distance(last, p) >= 2) pts.push(p);
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx) {
      if (pts.length === 0) return [];
      const set = lib.get(cfgStr(ctx.config, 'set', lib.firstId())) ?? lib.sets[0];
      if (!set || set.items.length === 0) return [];

      const spacing = cfgNum(ctx.config, 'spacing', 40);
      const size = cfgNum(ctx.config, 'size', 38);
      const scaleJitter = cfgNum(ctx.config, 'scaleJitter', 0);
      const rotJitter = cfgNum(ctx.config, 'rotJitter', 0);
      const align = cfgBool(ctx.config, 'align', false);

      let stamps = resampleByArcLength(pts, spacing);
      if (stamps.length > MAX_STAMPS) stamps = stamps.slice(0, MAX_STAMPS);

      const out: Shape[] = [];
      for (const st of stamps) {
        const item = set.items[ctx.rng.int(0, set.items.length - 1)] as ShapeItem | undefined;
        if (!item) continue;
        const scale = size * (1 + (ctx.rng.next() * 2 - 1) * scaleJitter);
        const angle = (align ? st.angle : 0) + (rotJitter > 0 ? (ctx.rng.next() * 2 - 1) * rotJitter * Math.PI : 0);
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);
        const path = transformPath(item.path, (q) => ({
          x: st.p.x + (q.x * cosA - q.y * sinA) * scale,
          y: st.p.y + (q.x * sinA + q.y * cosA) * scale,
        }));
        const style = defaultStyle();
        style.color = { ...ctx.ink };
        if (item.stroke) {
          style.mode = 'stroke';
          style.lineWidth = Math.max(0.75, size * 0.04);
        } else {
          style.mode = 'fill';
          style.fillRule = item.fillRule ?? 'nonzero';
        }
        out.push(ctx.newShape({ path, style }));
      }
      return out;
    },

    onClear() {
      pts = [];
    },

    onCommit() {
      pts = [];
    },
  };
}
